import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Spin, Input, Space } from "antd";
import useTargetList from "../../../hooks/useTargetList";
import useApp from "../../../hooks/useApp";
import useAuth from "../../../hooks/useAuth";
import { columns, DEFAULT_SOURCE_WINDOW } from "./config";
import {
  PlusOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import AddNewList from "./Add";

const TableTitle = ({
  sourceWindowActive,
  sourceWindow,
}: {
  sourceWindowActive: boolean;
  sourceWindow: number;
}) => (
  <>
    <h2>Target Lists</h2>
    {sourceWindowActive && (
      <div style={{ fontSize: 12, color: "rgba(0,0,0,0.45)" }}>
        Sorting/filtering by Source only looks at the last {sourceWindow}{" "}
        records — open the Source filter to change that number.
      </div>
    )}
  </>
);

function Home() {
  const targetList = useTargetList();
  const app = useApp();
  const { user } = useAuth();
  const [filteredData, setFilteredData] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sourceWindow, setSourceWindow] = useState(DEFAULT_SOURCE_WINDOW);
  // True while either the Source filter or the Source sort is engaged — in
  // both cases we need a real window of records (not just the default
  // 10-row page) for the interaction to be meaningful, and pagination over
  // that window has to stay client-side.
  const [sourceWindowActive, setSourceWindowActive] = useState(false);
  // Controls sort for BOTH the "Uploaded" and "Source" columns from one
  // place. Needed because once any column controls its own sortOrder, antd
  // stops honoring defaultSortOrder on the others — so the "Uploaded"
  // column's initial "newest first" state has to be driven the same way as
  // Source's, or it silently stops applying. Also gives the filter's
  // "Reset" button a way to actually clear Source's sort indicator, which
  // antd's own uncontrolled sort state has no external hook for.
  const DEFAULT_SORT = { field: "created", order: "descend" as const };
  const [sortState, setSortState] = useState<{
    field?: string;
    order?: "ascend" | "descend";
  }>(DEFAULT_SORT);
  const sourceSortOrder =
    sortState.field === "source" ? sortState.order : undefined;
  const uploadedSortOrder =
    sortState.field === "created" ? sortState.order : undefined;
  // antd only fires onChange from confirm()/clearFilters() when the filter
  // selection (selectedKeys) itself actually changed. That means clicking
  // OK after only editing the window number — or clicking Reset with no
  // checkbox ticked, e.g. after only sorting by Source — confirms an
  // unchanged selection and antd stays silent, so nothing downstream of
  // onChange ever runs. Both buttons therefore apply their effect directly
  // instead of waiting on that notification; this flag just tells
  // onTableChange to stand down if antd's onChange *does* also fire right
  // after (a real filter change was in fact applied/cleared), so the two
  // don't issue conflicting fetches.
  const suppressNextSourceFetchRef = useRef(false);
  useEffect(() => setFilteredData(targetList.data), [targetList.data]);

  const onCancel = () => setShowUploadModal(false);

  const suppressNextSourceFetch = () => {
    suppressNextSourceFetchRef.current = true;
    setTimeout(() => {
      suppressNextSourceFetchRef.current = false;
    }, 0);
  };

  const onResetSourceSort = () => {
    suppressNextSourceFetch();
    setSortState(DEFAULT_SORT);
    setSourceWindowActive(false);
    targetList.actions.getList({ query: {} });
  };

  const onApplySourceFilter = (selectedSourceKeys: string[]) => {
    suppressNextSourceFetch();
    const wasWindowActive = sourceWindowActive;
    const filterActive = selectedSourceKeys.length > 0;
    const sortActive = sortState.field === "source" && !!sortState.order;
    const windowActive = filterActive || sortActive;
    setSourceWindowActive(windowActive);
    if (windowActive) {
      targetList.actions.getList({ query: { limit: sourceWindow, page: 1 } });
    } else if (wasWindowActive) {
      // Unticking every checkbox (with no Source sort active either) turns
      // windowActive off — that's not "nothing to apply", it's "the filter
      // was just cleared", so the default list needs to come back instead
      // of leaving the previous windowed/filtered rows on screen.
      targetList.actions.getList({ query: {} });
    }
  };

  const onTableChange = (
    pagination: any,
    tablefilters: any,
    sorter: any,
    extra: any
  ) => {
    if (suppressNextSourceFetchRef.current) {
      // onApplySourceFilter/onResetSourceSort already handled this
      // directly — antd firing onChange right after (because the filter
      // selection did in fact change) is a redundant notification here,
      // not a new request.
      suppressNextSourceFetchRef.current = false;
      return;
    }

    const wasWindowActive = sourceWindowActive;
    const filterActive = !!tablefilters?.source?.length;
    // Sorting by Source only surfaces the (comparatively rare) Snowflake
    // rows if it has more than the current 10-row page to work with, so it
    // needs the same widened window as the filter — otherwise most pages
    // won't contain a single Snowflake record and the sort looks like it
    // does nothing.
    const sortActive = sorter?.field === "source" && !!sorter?.order;
    const windowActive = filterActive || sortActive;
    setSourceWindowActive(windowActive);
    setSortState({ field: sorter?.field, order: sorter?.order });

    if (searchText) return;
    // The Source filter's OK/Reset buttons apply their own fetch directly
    // (see onApplySourceFilter/onResetSourceSort above) — relying on this
    // "filter" action to carry the current window size isn't reliable,
    // since antd only raises it when the checkbox selection itself changed.
    if (extra?.action === "filter") return;
    // Paging through an already-fetched window is client-side — antd pages
    // the loaded rows itself, no need to refetch per page.
    if (extra?.action === "paginate" && windowActive) return;
    // Toggling sort direction over a window we already fetched doesn't
    // need a new request — the rows are already loaded, only the
    // client-side order changes.
    if (wasWindowActive && windowActive) return;

    const { current, pageSize } = pagination;
    const query: any = {
      query: {
        limit: windowActive ? sourceWindow : pageSize,
        page: windowActive ? 1 : current,
        ...(sorter?.field !== "source" &&
          sorter &&
          Object.keys(sorter) && {
            sort: sorter?.order === "ascend",
          }),
      },
    };

    targetList.actions.getList(query);
  };

  if (!targetList.listReady || !app.appReady)
    return (
      <div className="automations-container">
        <Spin size="large" />
      </div>
    );

  return (
    <div className="automations-container">
      {showUploadModal && (
        <AddNewList
          visible={showUploadModal}
          onCancel={onCancel}
          targetList={targetList}
          setVisible={setShowUploadModal}
          app={app}
          user={user}
        />
      )}
      <div className="add-automation-container">
        <Button
          className="button-primary"
          type="primary"
          onClick={() => setShowUploadModal(true)}
          icon={<PlusOutlined />}
        >
          Upload New List
        </Button>
      </div>

      <Space direction="horizontal" size={"large"}>
        <Input
          placeholder="Filter Target Lists"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Button
          className="button-primary"
          type="primary"
          onClick={() => {
            targetList.actions.getList({
              query: {
                searchQuery: searchText,
              },
            });
          }}
          icon={<SearchOutlined />}
        />
        <Button
          className="button-primary"
          type="primary"
          onClick={() => {
            setSearchText("");
            targetList.actions.getList({ query: {} });
          }}
          icon={<ReloadOutlined />}
        />
      </Space>
      <Table
        title={() => (
          <TableTitle
            sourceWindowActive={sourceWindowActive}
            sourceWindow={sourceWindow}
          />
        )}
        columns={columns(app, {
          sourceWindow,
          setSourceWindow,
          sourceSortOrder,
          uploadedSortOrder,
          onResetSort: onResetSourceSort,
          onApplyFilter: onApplySourceFilter,
        })}
        pagination={
          sourceWindowActive
            ? { pageSize: targetList.pagination.pageSize, showSizeChanger: false }
            : { ...targetList.pagination, showSizeChanger: false }
        }
        dataSource={filteredData}
        rowKey={(record) => record.pk}
        onChange={onTableChange}
        loading={targetList.isLoading}
      />
    </div>
  );
}

export default Home;
